import type { ReactNode } from 'react';
import { forwardRef, useMemo, useState } from 'react';
import mergeRefs from 'react-merge-refs';
import { useIsEnterpriseSubscriber } from '@frameio/shared/hooks';
import { EMPTY_OBJECT } from '@frameio/utils';
import { Chip, noop, theme, ThemeProvider } from '@frameio/vapor';
import { getTierConfig, themeProviderBrand } from '../config';
import { useCurrentPlanVersionAndTier } from '../hooks/useCurrentPlanVersionAndTier';
import { useCustomPopoverContext } from '../hooks/useCustomPopoverContext';
import { useFeatureGateDisableContext } from '../hooks/useFeatureGateDisableContext';
import { useFeatureGateLabelActiveContext } from '../hooks/useFeatureGateLabelActiveContext';
import type { FeatureInput } from '../hooks/useFeatureGatePlanSuggestion';
import { useFeatureGatePlanSuggestion } from '../hooks/useFeatureGatePlanSuggestion';
import styles from './FeatureGateLabel.module.css';
import { FeatureGateModalButtonWithTooltip } from './FeatureGateModalButtonWithTooltip';
import TEXT from './generated/TEXT/FeatureGateLabel';

TEXT.DEFINE({
  /** Label for Enterprise subscribers viewing features */
  addOn: 'Add-on',
  /** Label for retail users viewing version upgrades */
  upgrade: 'Upgrade',
});

type FeatureGateLabelProps = {
  /** Optionally provide a component to render when feature is available */
  fallback?: ReactNode;
  /** The feature we are checking */
  feature: FeatureInput;
  /** Whether this component should open upgrade modal on click/keyup */
  shouldOpenUpgradeModal?: boolean;
  /** Whether this component should show a tooltip on hover/focus */
  shouldRenderTooltip?: boolean;
  /** Optional styling for the upgrade modal button that wraps the Chip */
  wrapperStyles?: React.CSSProperties;
};

const getCta = ({
  isEnterpriseSubscriber = false,
  isSuggestingRetailVersionUpgrade = false,
  planTitle,
}: {
  isEnterpriseSubscriber: boolean;
  isSuggestingRetailVersionUpgrade: boolean;
  planTitle: string;
}) => {
  if (isEnterpriseSubscriber) {
    return TEXT.addOn;
  }

  if (isSuggestingRetailVersionUpgrade) {
    return TEXT.upgrade;
  }

  return planTitle;
};
/**
 * Displays the plan tier that is required to use a feature.
 */
export const FeatureGateLabel = forwardRef<
  HTMLDivElement | HTMLButtonElement,
  FeatureGateLabelProps
>(
  (
    {
      fallback = null,
      feature,
      shouldOpenUpgradeModal = true,
      shouldRenderTooltip = true,
      wrapperStyles = EMPTY_OBJECT,
    },
    forwardedRef
  ) => {
    const featureGateLabelActiveContext = useFeatureGateLabelActiveContext();
    const featureGateLabelActiveState = useState(false);
    const [isUpgradeModalButtonActive, setIsUpgradeModalButtonActive] =
      featureGateLabelActiveContext || featureGateLabelActiveState;
    const featureGateDisableContext = useFeatureGateDisableContext();
    const featureGatePlanSuggestion = useFeatureGatePlanSuggestion(feature);
    const customPopoverContext = useCustomPopoverContext();
    const isEnterpriseSubscriber = useIsEnterpriseSubscriber();
    const currentPlanVersionAndTier = useCurrentPlanVersionAndTier();

    const plan = featureGatePlanSuggestion?.includedInCurrentPlan
      ? undefined
      : featureGatePlanSuggestion?.plan;
    const tierConfig = plan ? getTierConfig(plan.tier) : undefined;

    const showActive =
      isUpgradeModalButtonActive ||
      !!featureGateDisableContext?.isUpgradeModalButtonActive ||
      !!customPopoverContext?.isPopoverShowing;

    const chipStyle = useMemo(
      () =>
        tierConfig
          ? ({
              '--fg-chip-bg': showActive
                ? tierConfig.color
                : tierConfig.colorLowOpacity,
              '--fg-chip-shadow': showActive
                ? `0px 0px 7px 2.66px ${tierConfig.colorLowOpacity}`
                : 'none',
              '--fg-chip-color': showActive
                ? theme.colors.components.button.primary.text
                : tierConfig.defaultTextColor,
              '--fg-chip-hover-bg': tierConfig.color,
              '--fg-chip-hover-shadow': `0px 0px 7px 2.66px ${tierConfig.colorLowOpacity}`,
              '--fg-chip-hover-color':
                theme.colors.components.button.primary.text,
            } as React.CSSProperties)
          : undefined,
      [showActive, tierConfig]
    );

    if (!plan || !tierConfig) return fallback;

    const planTitle = plan.title;
    const isSuggestingRetailVersionUpgrade =
      plan.version !== currentPlanVersionAndTier?.version &&
      plan.tier === currentPlanVersionAndTier?.tier &&
      plan.tier !== 'ENTERPRISE';

    return (
      <FeatureGateModalButtonWithTooltip
        feature={feature}
        isTooltipShowing={isUpgradeModalButtonActive}
        setIsUpgradeModalButtonActive={
          shouldRenderTooltip ? setIsUpgradeModalButtonActive : noop
        }
        shouldOpenUpgradeModal={shouldOpenUpgradeModal}
        shouldRenderTooltip={shouldRenderTooltip}
        wrapperStyles={wrapperStyles}
      >
        <ThemeProvider brand={themeProviderBrand}>
          <Chip
            ref={
              featureGateDisableContext?.tooltipAnchorRef
                ? mergeRefs([
                    featureGateDisableContext.tooltipAnchorRef,
                    forwardedRef,
                  ])
                : forwardedRef
            }
            className={styles.chip}
            iconBefore={<tierConfig.icon />}
            size="small"
            style={chipStyle}
          >
            {getCta({
              isEnterpriseSubscriber,
              isSuggestingRetailVersionUpgrade,
              planTitle,
            })}
          </Chip>
        </ThemeProvider>
      </FeatureGateModalButtonWithTooltip>
    );
  }
);
